import React from 'react'
import PropTypes from 'prop-types'
import { format } from 'date-fns'
import {
  HStack,
  Button,
  Text,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure
} from '@chakra-ui/react'
import { XCircleIcon, RocketLaunchIcon } from '@heroicons/react/24/outline'

import axios from 'lib/axios'
import ReportHeader from '../ReportHeader'
import CriminalReportDetails from './CriminalReportDetails'
import CreditReportDetails from './CreditReportDetails'
import EvictionReportDetails from './EvictionReportDetails'
import { useGetBackgroundCheckRequestByTypeQuery } from 'graphql/generated'
import { BackgroundCheckRequestType } from '@homevest/types/user-background-check-requests'

const reportTitles: Record<BackgroundCheckRequestType, string> = {
  credit_reports: 'Credit Report Data',
  eviction_reports: 'Eviction Report Data',
  criminal_reports: 'Criminal Report Data'
}

export default function BackgroundCheckReport({
  report,
  userId,
  reportType
}: {
  report: any
  userId: string
  reportType: BackgroundCheckRequestType
}) {
  const {
    isOpen: isInvalidateOpen,
    onOpen: onInvalidateOpen,
    onClose: onInvalidateClose
  } = useDisclosure()
  const { isOpen: isLaunchOpen, onOpen: onLaunchOpen, onClose: onLaunchClose } = useDisclosure()

  const [{ data }] = useGetBackgroundCheckRequestByTypeQuery({
    variables: {
      reportType: reportType,
      userId: userId
    }
  })
  const backgroundCheckRequest = data?.background_check_requests?.[0] || null

  const launchReport = async () => {
    try {
      await axios.post(`/admin/background_check_requests/${reportType}`, { user_id: userId })
      onLaunchClose()
    } catch (error) {
      console.error(`Error creating ${reportType} report: `, error)
    }
  }

  const scrubReport = async () => {
    try {
      await axios.post(`/admin/background_check_requests/scrub/${reportType}`, { user_id: userId })
      onInvalidateClose()
    } catch (error) {
      console.error(`Error invalidating ${reportType} report: `, error)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <HStack spacing={0}>
        <ReportHeader text={reportTitles[reportType]} />
        {!report || !backgroundCheckRequest || backgroundCheckRequest.invalidated_at ? (
          <Tooltip
            label={`Launch ${reportType.replace('_', ' ').replace(/s$/, '')}`}
            placement='right'
            bg='transparent'
            color='teal'
            fontSize='xs'
            p={2}
          >
            <Button size='xs' colorScheme='teal' variant='ghost' onClick={onLaunchOpen} mt={4}>
              <RocketLaunchIcon className='h-4 w-4' />
            </Button>
          </Tooltip>
        ) : (
          <Tooltip
            label={`Invalidate ${reportType.replace('_', ' ').replace(/s$/, '')}`}
            placement='right'
            bg='transparent'
            color='red.600'
            fontSize='xs'
            p={2}
          >
            <Button
              size='xs'
              colorScheme='red'
              variant='ghost'
              onClick={onInvalidateOpen}
              mt={4}
              isDisabled={isInvalidateOpen}
            >
              <XCircleIcon className='h-4 w-4' />
            </Button>
          </Tooltip>
        )}
      </HStack>

      <Modal isOpen={isLaunchOpen} onClose={onLaunchClose} size='xl' scrollBehavior='inside'>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Launch {reportType.replace('_', ' ').replace(/s$/, '')}</ModalHeader>
          <ModalFooter>
            <Button variant='ghost' size='xs' mr={3} onClick={onLaunchClose}>
              Cancel
            </Button>
            <Button colorScheme='teal' size='xs' onClick={launchReport}>
              Launch
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isInvalidateOpen}
        onClose={onInvalidateClose}
        size='xl'
        scrollBehavior='inside'
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Invalidate {reportType.replace('_', ' ').replace(/s$/, '')}</ModalHeader>
          <ModalBody>
            <div style={{ marginBottom: '1rem' }}>
              {reportType === 'credit_reports' && <CreditReportDetails creditReport={report} />}
              {reportType === 'eviction_reports' && (
                <EvictionReportDetails evictionReport={report} />
              )}
              {reportType === 'criminal_reports' && (
                <CriminalReportDetails criminalReport={report} />
              )}
            </div>
            <Text>Invalidate this {reportType.replace('_', ' ').replace(/s$/, '')}?</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant='ghost' size='xs' mr={3} onClick={onInvalidateClose}>
              Cancel
            </Button>
            <Button colorScheme='red' size='xs' onClick={scrubReport}>
              Invalidate
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {backgroundCheckRequest && (
        <>
          {backgroundCheckRequest.invalidated_at && (
            <Text color={'red.600'} fontSize={'xs'}>
              Invalidated:{' '}
              {format(new Date(backgroundCheckRequest.invalidated_at), 'MMM d, yyyy h:mm a')}
            </Text>
          )}
          {backgroundCheckRequest.status === 'pending' ? (
            <Text color={'yellow.600'} fontSize={'xs'}>
              Report pending...
            </Text>
          ) : backgroundCheckRequest.status === 'failed' ? (
            <Text color={'red.600'} fontSize={'xs'}>
              Report failed
              {backgroundCheckRequest.error_message && `: ${backgroundCheckRequest.error_message}`}
            </Text>
          ) : (
            <div style={{ paddingLeft: '20px', marginTop: '10px' }}>
              {reportType === 'credit_reports' && <CreditReportDetails creditReport={report} />}
              {reportType === 'eviction_reports' && (
                <EvictionReportDetails evictionReport={report} />
              )}
              {reportType === 'criminal_reports' && (
                <CriminalReportDetails criminalReport={report} />
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

BackgroundCheckReport.propTypes = {
  report: PropTypes.object,
  userId: PropTypes.string.isRequired,
  reportType: PropTypes.oneOf(['credit_reports', 'eviction_reports', 'criminal_reports']).isRequired
}
